import React, { useMemo, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Add,
  Close,
  Delete,
  Error as ErrorIcon
} from "@dreamhub/designsystem/icons";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { Divider } from "@/components/ui/divider";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import {
  BundleDiscountError,
  bundleDiscountedTotal,
  bundleLineTotal,
  bundleableTerms,
  parseBundleDiscount,
  parseBundleQuantity
} from "../bundleUtils";
import { useCreateProductBundle, useUpdateProductBundle } from "../hooks";
import {
  ProductBundle,
  ProductBundleInput,
  ProductBundleItem,
  ProductV2,
  ServiceItem,
  ServiceItemType
} from "../types";
import { formatPrice } from "../utils";
import { BundleDiscountField } from "./BundleDiscountField";
import { BundleServiceRow, BundleServiceSelection } from "./BundleServiceRow";

const TRANSLATION_KEY = "settings.product.bundleDrawer";
const NAME_INPUT_ID = "bundle-drawer-name";
const DESCRIPTION_INPUT_ID = "bundle-drawer-description";
// The backend's limits.
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 1024;

interface BundleDrawerProps {
  product: ProductV2;
  /** The bundle to edit; omitted when adding one. */
  bundle?: ProductBundle;
  serviceItemTypes: ServiceItemType[];
  onClose: () => void;
  onDelete: (bundle: ProductBundle) => void;
}

// A bundle's items the drawer can show: a service since deleted, or a term no
// longer bundleable, has no row to uncheck and would only fail the save.
const editableItems = (
  bundle: ProductBundle | undefined,
  services: ServiceItem[]
): ProductBundleItem[] =>
  (bundle?.items ?? []).filter((item) => {
    const service = services.find(({ id }) => id === item.serviceItemId);
    return Boolean(
      service &&
        bundleableTerms(service).some(
          ({ id }) => id === item.serviceItemPricingId
        )
    );
  });

// A 400 also covers a service the product no longer offers, so only the
// discount's own refusal goes under the discount field.
const isDiscountAboveTotal = (status?: number, data?: { detail?: unknown }) =>
  status === 400 &&
  typeof data?.detail === "string" &&
  data.detail.startsWith("The discount of");

export const BundleDrawer: React.FC<BundleDrawerProps> = ({
  product,
  bundle,
  serviceItemTypes,
  onClose,
  onDelete
}) => {
  const { t } = useTranslation();
  const isEditing = Boolean(bundle);
  const createBundle = useCreateProductBundle();
  const updateBundle = useUpdateProductBundle();
  const isSaving = createBundle.isPending || updateBundle.isPending;

  const services = useMemo(
    () =>
      product.serviceItems
        .filter((service) => !service.isDeleted)
        .sort((a, b) => a.itemOrder - b.itemOrder),
    [product.serviceItems]
  );

  const [name, setName] = useState(bundle?.name ?? "");
  const [description, setDescription] = useState(bundle?.description ?? "");
  const [selections, setSelections] = useState<
    Record<string, BundleServiceSelection>
  >(() =>
    Object.fromEntries(
      editableItems(bundle, services).map((item) => [
        item.serviceItemId,
        {
          termId: item.serviceItemPricingId,
          quantity: String(item.quantity)
        }
      ])
    )
  );
  // The bundle's own items first, in their order, then newly checked ones.
  const [itemOrder, setItemOrder] = useState<string[]>(() =>
    editableItems(bundle, services).map((item) => item.serviceItemId)
  );
  const [isDiscountShown, setIsDiscountShown] = useState(
    Boolean(bundle?.discountAmount)
  );
  const [isDiscountPercentage, setIsDiscountPercentage] = useState(
    bundle?.isDiscountPercentage ?? true
  );
  const [discountValue, setDiscountValue] = useState(
    bundle?.discountAmount ? String(bundle.discountAmount) : ""
  );
  // A number field reports unparseable text ("-", "1e") as empty, which would
  // otherwise read as no discount and clear a stored one on save.
  const [isDiscountUnparseable, setIsDiscountUnparseable] = useState(false);
  // The backend's own refusal, kept only while the total and the discount it
  // refused are still on screen.
  const [refusedDiscount, setRefusedDiscount] = useState<{
    total: number;
    value: string;
  } | null>(null);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [isNameTaken, setIsNameTaken] = useState(false);
  const [pendingInput, setPendingInput] = useState<ProductBundleInput | null>(
    null
  );

  const nameError = isNameTaken
    ? t(`${TRANSLATION_KEY}.nameTaken`)
    : isSubmitAttempted && !name.trim()
      ? t("requiredField")
      : undefined;

  const typeName = (typeId: number) =>
    serviceItemTypes.find(({ id }) => id === typeId)?.name ?? "";

  const toggleService = (serviceId: string, checked: boolean) => {
    const service = services.find(({ id }) => id === serviceId);
    const firstTerm = service ? bundleableTerms(service)[0] : undefined;
    setSelections((current) => {
      const next = { ...current };
      if (checked && firstTerm) {
        next[serviceId] = { termId: firstTerm.id, quantity: "1" };
      } else {
        delete next[serviceId];
      }
      return next;
    });
    setItemOrder((order) =>
      checked
        ? [...order.filter((id) => id !== serviceId), serviceId]
        : order.filter((id) => id !== serviceId)
    );
  };

  const updateSelection = (
    serviceId: string,
    change: Partial<BundleServiceSelection>
  ) =>
    setSelections((current) => ({
      ...current,
      [serviceId]: { ...current[serviceId], ...change }
    }));

  const checkedServiceIds = itemOrder.filter((id) => selections[id]);

  const total = checkedServiceIds.reduce((sum, serviceId) => {
    const service = services.find(({ id }) => id === serviceId);
    const term = service?.pricing.find(
      ({ id }) => id === selections[serviceId].termId
    );
    if (!service || !term) return sum;
    return (
      sum +
      bundleLineTotal(
        service.pricingModel,
        service.chargeType,
        term,
        parseBundleQuantity(selections[serviceId].quantity) ?? 0
      )
    );
  }, 0);

  const discount: { amount: number | null; error: BundleDiscountError | null } =
    !isDiscountShown
      ? { amount: null, error: null }
      : isDiscountUnparseable
        ? {
            amount: null,
            error: isDiscountPercentage ? "percentage" : "negative"
          }
        : parseBundleDiscount(discountValue, isDiscountPercentage, total);
  const isDiscountRefused =
    refusedDiscount?.total === total && refusedDiscount.value === discountValue;
  const discountError: BundleDiscountError | null =
    discount.error ?? (isDiscountRefused ? "aboveTotal" : null);
  const discountedTotal = bundleDiscountedTotal(
    total,
    discount.amount,
    isDiscountPercentage
  );
  const hasDiscount = Boolean(discount.amount);

  const hasNoServices = checkedServiceIds.length === 0;
  const hasInvalidQuantity = checkedServiceIds.some(
    (id) => parseBundleQuantity(selections[id].quantity) === null
  );

  const save = async (input: ProductBundleInput) => {
    try {
      if (bundle) {
        await updateBundle.mutateAsync({
          pathParams: { productId: product.id, bundleId: bundle.id },
          data: input
        });
      } else {
        await createBundle.mutateAsync({
          pathParams: { productId: product.id },
          data: input
        });
      }
      onClose();
    } catch (error) {
      const response = (error as AxiosError<{ detail?: unknown }>)?.response;
      if (response?.status === 409) {
        setIsNameTaken(true);
      } else if (isDiscountAboveTotal(response?.status, response?.data)) {
        setRefusedDiscount({ total, value: discountValue });
      } else {
        toast.error(t(`${TRANSLATION_KEY}.saveError`));
      }
    }
  };

  const handleSave = () => {
    setIsSubmitAttempted(true);
    if (
      isSaving ||
      !name.trim() ||
      hasNoServices ||
      hasInvalidQuantity ||
      discountError
    ) {
      return;
    }
    const input: ProductBundleInput = {
      name: name.trim(),
      description: description.trim() || null,
      items: checkedServiceIds.map((serviceItemId) => ({
        serviceItemId,
        serviceItemPricingId: selections[serviceItemId].termId,
        quantity: parseBundleQuantity(selections[serviceItemId].quantity) ?? 0
      })),
      // Always sent, since an update that leaves both out keeps the stored one.
      isDiscountPercentage: hasDiscount ? isDiscountPercentage : null,
      discountAmount: hasDiscount ? discount.amount : null
    };
    if (isEditing) {
      setPendingInput(input);
    } else {
      save(input);
    }
  };

  const handleConfirmUpdate = () => {
    if (!pendingInput) return;
    const input = pendingInput;
    setPendingInput(null);
    save(input);
  };

  return (
    <Drawer
      open
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={false}
      noBodyStyles
      dismissible={false}
    >
      <AppDrawerContent
        className="w-full overflow-x-hidden p-0"
        data-test="bundle-drawer"
        isSettingsDrawer
      >
        <DrawerHeader>
          <DrawerTitle>
            <Text variant="lgSubtitle" data-test="bundle-drawer-title">
              {t(`${TRANSLATION_KEY}.${isEditing ? "editTitle" : "addTitle"}`)}
            </Text>
          </DrawerTitle>
          <IconButton
            onClick={onClose}
            // A save still in flight closes the drawer when it lands.
            disabled={isSaving}
            variant="iconNoBg"
            size="md"
            aria-label={t("close")}
            data-test="bundle-drawer-close-button"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="flex w-full flex-col overflow-y-scroll p-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label
                htmlFor={NAME_INPUT_ID}
                className="flex min-h-6 items-center"
              >
                <Text as="span" variant="bodySecondary">
                  {t(`${TRANSLATION_KEY}.name`)}
                </Text>
                <span className="ml-1 text-primary" aria-hidden="true">
                  *
                </span>
              </label>
              <Input
                id={NAME_INPUT_ID}
                value={name}
                aria-required="true"
                onChange={(event) => {
                  setName(event.target.value);
                  setIsNameTaken(false);
                }}
                placeholder={t(`${TRANSLATION_KEY}.namePlaceholder`)}
                maxLength={NAME_MAX_LENGTH}
                hasError={Boolean(nameError)}
                aria-invalid={Boolean(nameError)}
                aria-describedby={
                  nameError ? `${NAME_INPUT_ID}-error` : undefined
                }
                data-test="bundle-drawer-name-input"
              />
              {nameError && (
                <Text
                  id={`${NAME_INPUT_ID}-error`}
                  variant="smBodyError"
                  data-test="bundle-drawer-name-error"
                >
                  {nameError}
                </Text>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor={DESCRIPTION_INPUT_ID}
                className="flex min-h-6 items-center"
              >
                <Text as="span" variant="bodySecondary">
                  {t(`${TRANSLATION_KEY}.description`)}
                </Text>
              </label>
              <TextareaAutosize
                id={DESCRIPTION_INPUT_ID}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t(`${TRANSLATION_KEY}.descriptionPlaceholder`)}
                maxLength={DESCRIPTION_MAX_LENGTH}
                minRows={1}
                maxRows={6}
                className="w-full rounded-lg border-[0.5px] border-border-field-default bg-surface-field-default px-2 py-[6px] text-base font-normal leading-[18px] shadow-field outline-none placeholder:text-tertiary hover:border-border-field-hover focus:border-border-field-focus focus:bg-surface-focus-selected focus:shadow-field-focus"
                data-test="bundle-drawer-description-input"
              />
            </div>
          </div>
          <Divider dividerYPadding="16" />
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1 pl-1">
              <Text variant="bodySecondary">
                {t(`${TRANSLATION_KEY}.services`)}
              </Text>
              <Text variant="bodyTertiary" className="whitespace-pre-line">
                {t(`${TRANSLATION_KEY}.servicesHint`)}
              </Text>
            </div>
            <div
              className="overflow-hidden rounded-xl border-[0.5px] border-border-border1 bg-surface-field-default"
              data-test="bundle-drawer-services"
            >
              <div className="flex h-9 items-end border-b-[0.5px] border-border-border1 pb-1">
                <div className="w-10 shrink-0" />
                <Text variant="smBodySecondary" className="flex-1 px-2.5">
                  {t(`${TRANSLATION_KEY}.table.service`)}
                </Text>
                <Text
                  variant="smBodySecondary"
                  className="w-[70px] px-2.5 text-right"
                >
                  {t(`${TRANSLATION_KEY}.table.quantity`)}
                </Text>
              </div>
              {services.map((service) => (
                <BundleServiceRow
                  key={service.id}
                  service={service}
                  typeName={typeName(service.typeId)}
                  selection={selections[service.id]}
                  onToggle={(checked) => toggleService(service.id, checked)}
                  onTermChange={(termId) =>
                    updateSelection(service.id, { termId })
                  }
                  onQuantityChange={(quantity) =>
                    updateSelection(service.id, { quantity })
                  }
                />
              ))}
            </div>
            {isSubmitAttempted && hasNoServices && (
              <div
                className="flex items-center gap-1 pl-1"
                data-test="bundle-drawer-no-services-error"
              >
                <IconContainer as="span" color="error">
                  <ErrorIcon />
                </IconContainer>
                <Text variant="smBodyError">
                  {t(`${TRANSLATION_KEY}.noServicesError`)}
                </Text>
              </div>
            )}
          </div>
          <div className="pt-4">
            {isDiscountShown ? (
              <BundleDiscountField
                value={discountValue}
                isPercentage={isDiscountPercentage}
                error={discountError}
                onValueChange={setDiscountValue}
                onUnparseableChange={setIsDiscountUnparseable}
                onTypeChange={(isPercentage) => {
                  // The tabs re-report the active type on a re-click.
                  if (isPercentage === isDiscountPercentage) return;
                  setIsDiscountPercentage(isPercentage);
                  setDiscountValue("");
                  setIsDiscountUnparseable(false);
                }}
                onRemove={() => {
                  setIsDiscountShown(false);
                  setDiscountValue("");
                  setIsDiscountUnparseable(false);
                }}
              />
            ) : (
              <Button
                variant="tertiary"
                onClick={() => setIsDiscountShown(true)}
                data-test="bundle-drawer-add-discount"
              >
                <IconContainer as="span" size="sm" color="current">
                  <Add />
                </IconContainer>
                {t(`${TRANSLATION_KEY}.discount.add`)}
              </Button>
            )}
          </div>
          <Divider dividerYPadding="16" />
          <div className="flex flex-col pl-1" data-test="bundle-drawer-total">
            <Text variant="bodySecondary">{t(`${TRANSLATION_KEY}.total`)}</Text>
            <div className="flex h-8 items-center justify-between">
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.serviceCount`, {
                  count: checkedServiceIds.length
                })}
              </Text>
              <div className="flex items-center gap-1">
                {hasDiscount && (
                  <Text
                    variant="bodyTertiary"
                    className="line-through"
                    data-test="bundle-drawer-original-total"
                  >
                    {formatPrice(total)}
                  </Text>
                )}
                <Text variant="body" data-test="bundle-drawer-total-amount">
                  {formatPrice(discountedTotal)}
                </Text>
              </div>
            </div>
          </div>
        </div>

        <DrawerFooter>
          <div className="flex w-full justify-between gap-2 border-t-[0.5px] border-border-border1 p-4">
            {bundle && (
              <Button
                variant="tertiary"
                destructive
                onClick={() => onDelete(bundle)}
                disabled={isSaving}
                data-test="bundle-drawer-delete-button"
              >
                <IconContainer as="span" size="sm" color="current">
                  <Delete />
                </IconContainer>
                {t(`${TRANSLATION_KEY}.deleteBundle`)}
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondaryNoBg"
                onClick={onClose}
                disabled={isSaving}
                data-test="bundle-drawer-cancel-button"
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                data-test="bundle-drawer-submit-button"
              >
                {isEditing
                  ? t("save")
                  : t("settings.product.bundlesTab.addBundle")}
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </AppDrawerContent>

      <ConfirmationModal
        isOpen={Boolean(pendingInput)}
        setIsOpen={(open) => {
          if (!open) setPendingInput(null);
        }}
        title={t(`${TRANSLATION_KEY}.updateModal.title`)}
        confirmationText={t(`${TRANSLATION_KEY}.updateModal.description`)}
        submitButtonText={t(`${TRANSLATION_KEY}.updateModal.submit`)}
        handleSubmit={handleConfirmUpdate}
        handleCancel={() => setPendingInput(null)}
        isPrimaryButton
        hideDivider
        data-test="bundle-drawer-update-modal"
      />
    </Drawer>
  );
};
